import { useEffect, useRef, useState } from "react"
import { Input } from "components/ui/inputs/Input"
import { PlusIcon } from "components/ui/icons/plus/PlusIcon"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { Select } from "components/ui/select/Select"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import type { TCreateEcallRequest } from "types/elevationReferences.type"
import style from "./CreateEcallSidebar.module.scss"

const MAX_ELEVATION_REFERENCES = 4

type ElevationReferenceDraft = {
	elevationNumber: string
	referenceLabel: string
}

type Props = {
	area: { pageNumber: number } | null
	pageName: string | null
	pageNames: string[]
	isPending?: boolean
	onCancel: () => void
	onClose: () => void
	onCreate: (roomName: string, elevations: TCreateEcallRequest["elevations"], pageName: string) => void
}

/** Form shown while creating an elevation callout. The page comes from the drawn area. */
export const CreateEcallSidebar = ({
	area,
	pageName,
	pageNames,
	isPending = false,
	onCancel,
	onClose,
	onCreate,
}: Props) => {
	const [roomName, setRoomName] = useState("")
	const [referencingPage, setReferencingPage] = useState<string | null>(pageName)
	const [elevationReferences, setElevationReferences] = useState<ElevationReferenceDraft[]>([
		{ elevationNumber: "", referenceLabel: "" },
	])
	const roomNameRef = useRef<HTMLLabelElement>(null)
	useEffect(() => setReferencingPage(pageName), [pageName])

	const canCreate =
		!!area &&
		!!referencingPage &&
		elevationReferences.every((reference) => reference.elevationNumber.trim().length > 0) &&
		!isPending
	const updateReference = (index: number, field: keyof ElevationReferenceDraft, value: string) => {
		setElevationReferences((references) =>
			references.map((reference, referenceIndex) =>
				referenceIndex === index ? { ...reference, [field]: value } : reference,
			),
		)
	}
	const removeReference = (index: number) => {
		setElevationReferences((references) => references.filter((_, referenceIndex) => referenceIndex !== index))
	}
	const addReference = () => {
		setElevationReferences((references) => [...references, { elevationNumber: "", referenceLabel: "" }])
	}
	return (
		<CreateAreaSidebar
			title="Elevation callout"
			hint="Drag on the drawing to define the callout area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create callout"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={() =>
				onCreate(
					roomName.trim(),
					elevationReferences.map((reference) => ({
						elevation_number: reference.elevationNumber.trim(),
						reference_label: reference.referenceLabel.trim() || null,
					})),
					referencingPage ?? "",
				)
			}
			focusTargetRef={roomNameRef}
			sidebarDataAttribute="data-elevation-callout-sidebar"
		>
			<div className={style.fields}>
				<label className={style.inputBlock} ref={roomNameRef}>
					<span className={style.label}>Room name</span>
					<Input className={style.input} value={roomName} onChange={(e) => setRoomName(e.target.value)} />
				</label>
				<label className={style.inputBlock}>
					<span className={style.label}>Referencing page</span>
					<Select
						variant="outlined"
						options={pageNames.map((name) => ({ label: name, value: name }))}
						value={referencingPage}
						onChange={(value) => {
							if (value !== undefined && value !== null) setReferencingPage(String(value))
						}}
						onSearch={(value) => {
							if (value.trim().length > 0) setReferencingPage(value)
						}}
						placeholder="Select page"
						showSearch
						optionFilterProp="label"
						renderInPlace
					/>
				</label>
			</div>
			<div className={style.references}>
				<div className={style.referencesHeader}>
					<strong>Elevation references</strong>
					{elevationReferences.length < MAX_ELEVATION_REFERENCES ? (
						<button
							type="button"
							className={style.addReference}
							onClick={addReference}
							aria-label="Add elevation reference"
						>
										<PlusIcon size={16} color="currentColor" strokeWidth={2.4} />
						</button>
					) : null}
				</div>
				{elevationReferences.map((reference, index) => (
					<div className={style.referenceRow} key={index}>
						<label className={style.referenceInputBlock}>
							<span>Elevation number</span>
							<Input
								className={style.referenceInput}
								value={reference.elevationNumber}
								onChange={(event) => updateReference(index, "elevationNumber", event.target.value)}
							/>
						</label>
						<label className={style.referenceInputBlock}>
							<span>Reference label</span>
							<Input
								className={style.referenceInput}
								value={reference.referenceLabel}
								onChange={(event) => updateReference(index, "referenceLabel", event.target.value)}
							/>
						</label>
						<button
							type="button"
							className={style.removeReference}
							onClick={() => removeReference(index)}
							aria-label={`Remove elevation reference ${index + 1}`}
							disabled={elevationReferences.length === 1}
						>
										<CloseIcon size={12} color="currentColor" />
						</button>
					</div>
				))}
			</div>
		</CreateAreaSidebar>
	)
}
